import React, { useState, useEffect } from 'react';
import { Box, Title, Table, Button, Badge, Group, Text, Alert, ActionIcon, Modal, TextInput, Textarea, NumberInput, FileInput, Stack } from '@mantine/core';
import { IconCheck, IconX, IconVideo, IconTrash, IconEdit } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.ailinkb.shop';

export default function MySkillsTab({ user }) {
  const { t } = useTranslation();
  const [skills, setSkills] = useState([]);
  const [error, setError] = useState('');
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editPrice, setEditPrice] = useState(9.99);
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editPackageFile, setEditPackageFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.email) {
      fetchSkills();
    }
  }, [user]);

  const fetchSkills = () => {
    fetch(`${API_BASE_URL}/api/skills/my?ownerEmail=${encodeURIComponent(user.email)}`)
      .then(res => res.json())
      .then(data => {
        if (data.error) setError(data.error);
        else setSkills(data);
      })
      .catch(err => setError(err.toString()));
  };

  const deleteSkill = (id) => {
    if (!window.confirm(t('confirm_delete_skill', 'Czy na pewno chcesz usunąć ten skill ze sprzedaży?'))) return;
    
    fetch(`${API_BASE_URL}/api/skills/${id}?ownerEmail=${encodeURIComponent(user.email)}`, {
      method: 'DELETE'
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          fetchSkills();
        } else {
          setError(data.error);
        }
      })
      .catch(err => setError(err.toString()));
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    setEditTitle(skill.title || '');
    setEditDescription(skill.description || '');
    setEditTags(skill.tags || '');
    setEditPrice(skill.price || 9.99);
    setEditVideoFile(null);
    setEditPackageFile(null);
    setEditModalOpen(true);
  };

  const handleUpdate = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('title', editTitle);
    formData.append('description', editDescription);
    formData.append('tags', editTags);
    formData.append('price', editPrice);
    formData.append('ownerEmail', user.email);
    if (editPackageFile) formData.append('packageFile', editPackageFile);
    if (editVideoFile) formData.append('videoFile', editVideoFile);

    fetch(`${API_BASE_URL}/api/skills/${editingSkill.id}/update`, {
      method: 'PUT',
      body: formData
    })
    .then(res => res.json())
    .then(data => {
      setLoading(false);
      if (data.success) {
        setEditModalOpen(false);
        fetchSkills();
      } else {
        alert(data.error || t("generic_error", "Wystąpił błąd"));
      }
    })
    .catch(err => {
      setLoading(false);
      alert(t("connection_error", "Błąd połączenia: ") + err);
    });
  };

  return (
    <Box p="md">
      <Title order={3} mb="md">{t('my_published_skills', 'Moje wystawione Skille AI')}</Title>
      {error && <Alert color="red" mb="md" variant="light" icon={<IconX size={16} />}>{error}</Alert>}
      
      {skills.length === 0 ? (
        <Box 
          p="xl" 
          style={{ 
            border: '1px dashed #374151', 
            borderRadius: '12px', 
            textAlign: 'center', 
            backgroundColor: 'rgba(31, 41, 55, 0.2)',
            marginTop: '2rem',
            padding: '4rem 2rem'
          }}
        >
          <IconVideo size={48} color="#6b7280" style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <Title order={3} c="white" mb="sm">{t('no_skills_yet_title', 'Brak wystawionych skilli')}</Title>
          <Text c="dimmed" mb="lg" size="lg" maw={500} mx="auto">
            {t('no_skills_yet_desc', 'Nie sprzedajesz jeszcze żadnych umiejętności. Dodaj swoje pierwsze wideo z promptem AI lub agentem, aby zacząć zarabiać w Marketplace.')}
          </Text>
        </Box>
      ) : (
        <Box style={{ overflowX: 'auto', backgroundColor: '#111827', borderRadius: '12px', border: '1px solid #1f2937' }} mt="xl">
          <Table verticalSpacing="md" horizontalSpacing="lg" highlightOnHover style={{ minWidth: 700 }}>
            <Table.Thead style={{ backgroundColor: 'rgba(31, 41, 55, 0.5)' }}>
              <Table.Tr>
                <Table.Th style={{ borderBottom: '1px solid #374151', color: '#9ca3af', fontWeight: 600 }}>{t('skill_title', 'Tytuł')}</Table.Th>
                <Table.Th style={{ borderBottom: '1px solid #374151', color: '#9ca3af', fontWeight: 600 }}>{t('table_video', 'Wideo')}</Table.Th>
                <Table.Th style={{ borderBottom: '1px solid #374151', color: '#9ca3af', fontWeight: 600 }}>{t('skill_price', 'Cena')}</Table.Th>
                <Table.Th style={{ borderBottom: '1px solid #374151', color: '#9ca3af', fontWeight: 600 }}>{t('table_status', 'Status')}</Table.Th>
                <Table.Th style={{ borderBottom: '1px solid #374151', color: '#9ca3af', fontWeight: 600, textAlign: 'right' }}>{t('table_actions', 'Akcje')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {skills.map(skill => (
                <Table.Tr key={skill.id} style={{ borderBottom: '1px solid #1f2937' }}>
                  <Table.Td>
                    <Text fw={600} c="white">{skill.title}</Text>
                    {skill.tags && <Text size="xs" c="dimmed" mt={4}>{skill.tags.split(',').slice(0,3).join(' • ')}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <a href={skill.videoUrl?.startsWith('/') ? `${API_BASE_URL}${skill.videoUrl}` : skill.videoUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                      <Button variant="light" color="indigo" size="xs" radius="xl" leftSection={<IconVideo size={14} />}>
                        {t('preview', 'Podgląd')}
                      </Button>
                    </a>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="dot" color="gray" size="lg" radius="sm">€{Number(skill.price).toFixed(2)}</Badge>
                  </Table.Td>
                  <Table.Td>
                    {skill.status === 'APPROVED' && <Badge color="green" variant="light" size="sm">{t('status_approved', 'Opublikowany')}</Badge>}
                    {skill.status === 'PENDING' && <Badge color="yellow" variant="light" size="sm">{t('status_pending', 'Weryfikacja')}</Badge>}
                    {skill.status === 'REJECTED' && <Badge color="red" variant="light" size="sm">{t('status_rejected', 'Odrzucony')}</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <ActionIcon color="blue" variant="light" radius="md" size="lg" onClick={() => openEditModal(skill)} title={t('edit_skill', 'Edytuj')}>
                        <IconEdit size={18} />
                      </ActionIcon>
                      <ActionIcon color="red" variant="light" radius="md" size="lg" onClick={() => deleteSkill(skill.id)} title={t('delete', 'Usuń')}>
                        <IconTrash size={18} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} title={t('edit_skill', 'Edytuj Skill')} size="lg">
        <Stack gap="md">
          <TextInput label={t("skill_title", "Tytuł")} value={editTitle} onChange={e => setEditTitle(e.target.value)} />
          <TextInput label={t("skill_tags", "Tagi")} value={editTags} onChange={e => setEditTags(e.target.value)} />
          <Textarea label={t("skill_description", "Opis")} value={editDescription} onChange={e => setEditDescription(e.target.value)} minRows={4} />
          <NumberInput label={t("skill_price", "Cena (EUR)")} min={0} value={editPrice} onChange={v => setEditPrice(v)} />
          
          <FileInput label={t("update_video_file", "Zaktualizuj wideo (opcjonalnie)")} accept="video/mp4" placeholder={t("click_to_select", "Kliknij by wybrać...")} value={editVideoFile} onChange={setEditVideoFile} clearable />
          <FileInput label={t("update_package_file", "Zaktualizuj paczkę (opcjonalnie)")} placeholder={t("click_to_select", "Kliknij by wybrać...")} value={editPackageFile} onChange={setEditPackageFile} clearable />
          
          <Button loading={loading} onClick={handleUpdate} color="blue" fullWidth>{t("save_changes", "Zapisz Zmiany")}</Button>
        </Stack>
      </Modal>
    </Box>
  );
}
